import { useEffect, useRef, useState } from "react";
import { Box, FileText, ListChecks, Loader2, ReceiptText, Search, Truck, Users, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { api } from "@/api";

const EMPTY_RESULTS = { clients: [], quotes: [], invoices: [], tasks: [], suppliers: [], warehouse_products: [] };

const SECTIONS = [
  { key: "clients", label: "Clienti", icon: Users, testId: "global-search-clients" },
  { key: "quotes", label: "Preventivi", icon: FileText, testId: "global-search-quotes" },
  { key: "invoices", label: "Fatture e proforma", icon: ReceiptText, testId: "global-search-invoices" },
  { key: "tasks", label: "Task", icon: ListChecks, testId: "global-search-tasks" },
  { key: "suppliers", label: "Fornitori", icon: Truck, testId: "global-search-suppliers" },
  { key: "warehouse_products", label: "Magazzino", icon: Box, testId: "global-search-warehouse" },
];

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: trimmed } });
        setResults({ ...EMPTY_RESULTS, ...data });
      } catch {
        setResults(EMPTY_RESULTS);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const clear = () => { setQuery(""); setResults(EMPTY_RESULTS); setOpen(false); };

  const goTo = (key, item) => {
    if (key === "clients") navigate(`/clienti/${item.id}/storico`);
    else if (key === "quotes") navigate(`/preventivi/${item.id}/modifica`);
    else if (key === "invoices") navigate(item.subtitle ? `/fatture?cliente=${encodeURIComponent(item.subtitle)}` : "/fatture");
    else if (key === "tasks") navigate(`/task?task=${item.id}`);
    else if (key === "suppliers") navigate("/fornitori");
    else navigate("/magazzino");
    clear();
  };

  const trimmed = query.trim();
  const totalResults = Object.values(results).reduce((sum, items) => sum + items.length, 0);
  const showPanel = open && trimmed.length >= 2;

  return (
    <div className="global-search-bar" data-testid="global-search-bar" ref={containerRef}>
      <div className="global-search-input-wrap">
        <Search size={17} className="global-search-icon" />
        <input
          data-testid="global-search-input"
          type="text"
          placeholder="Cerca clienti, preventivi, fatture, task, fornitori o magazzino..."
          value={query}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {loading && <Loader2 size={16} className="global-search-spinner spin" />}
        {query && (
          <button type="button" data-testid="global-search-clear" className="global-search-clear" onClick={clear} aria-label="Svuota ricerca">
            <X size={15} />
          </button>
        )}
      </div>
      {showPanel && (
        <div className="global-search-results" data-testid="global-search-results">
          {trimmed.length < 2 ? null : loading && totalResults === 0 ? (
            <p className="global-search-empty">Ricerca in corso...</p>
          ) : totalResults === 0 ? (
            <p className="global-search-empty" data-testid="global-search-no-results">Nessun risultato per «{trimmed}»</p>
          ) : (
            SECTIONS.map(({ key, label, icon: Icon, testId }) => {
              const items = results[key] || [];
              if (!items.length) return null;
              return (
                <div className="global-search-section" key={key} data-testid={testId}>
                  <p className="global-search-section-title"><Icon size={13} />{label}</p>
                  {items.map((item) => (
                    <button
                      type="button"
                      key={`${key}-${item.id}`}
                      className="global-search-result"
                      data-testid={`global-search-result-${key}-${item.id}`}
                      onClick={() => goTo(key, item)}
                    >
                      <strong>{item.title}</strong>
                      {item.subtitle && <span>{item.subtitle}</span>}
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
